import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { UserBase } from '@core/account-types';
import { InterviewAnswerListComponent } from '@core/components-interview-answer-list-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultsActions } from '@core/interview-results-data';
import type {
  InterviewResult,
  InterviewResultAnswer,
  RatingLevelTranslations,
} from '@core/interview-results-types';
import { InterviewEvaluationRatingComponent } from '@core/interview-results-ui';
import { AppNavigatorService } from '@core/navigation-legacy';
import { provideFilteredMentions } from '@core/notes-legacy';
import type { OfferFull } from '@core/offers-types';
import { OfferKind } from '@core/offers-types';
import { OfferStatusIndicatorComponent, PlacementInfo } from '@core/offers-ui';
import { RenderDirective, WysiwygContext$ } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorDefaultConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import type { Stage } from '@core/stages-types';
import {
  AvatarComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogService,
  DividerComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtStatefulComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { InterviewKind } from '../../interview-kind';
import { QuickEvaluationCacheService } from '../../quick-evaluation-cache.service';
import { QuickEvaluationFormComponent } from '../../quick-evaluation-form/quick-evaluation-form.component';
import { InterviewMemberComponent } from '../interview-member/interview-member.component';

type State = {
  showMore: boolean;
  showMoreEnabled: boolean;
  quickEditEnabled: boolean;
  deleteLoading: boolean;
  answers: InterviewResultAnswer[];
};

@Component({
  selector: 'rt-interview-result-card',
  templateUrl: 'interview-result-card.component.html',
  styleUrls: ['./interview-result-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    NgTemplateOutlet,
    DividerComponent,
    HeadingComponent,
    InterviewAnswerListComponent,
    ButtonComponent,
    QuickEvaluationFormComponent,
    InterviewEvaluationRatingComponent,
    InterviewMemberComponent,
    GroupComponent,
    IconButtonComponent,
    ButtonLoadingDirective,
    AvatarComponent,
    OfferStatusIndicatorComponent,
    TranslatePipe,
    IconComponent,
    RenderDirective,
    ProsemirrorDefaultConfigDirective,
  ],
  providers: [
    QuickEvaluationCacheService,
    { provide: WysiwygContext$, useFactory: () => new WysiwygContext$(null) },
    ...provideFilteredMentions(),
  ],
})
export class InterviewResultCardComponent extends RtStatefulComponent<State> {
  private readonly dialogService = inject(DialogService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly appNavigator = inject(AppNavigatorService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  private readonly MAX_VISIBLE_HEIGHT: number = 240;
  protected readonly cdRef: ChangeDetectorRef;
  protected readonly ProsemirrorConfig = ProsemirrorDefaultConfig;
  protected readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);
  public readonly sanitizer = inject(DomSanitizer);

  public readonly candidate = input<{
    id: number;
  }>(undefined!);
  public readonly edit = input<boolean>(true);
  public readonly currentAdmin = input<UserBase>();
  public readonly hasHeader = input<boolean>(false);
  public readonly isFairEvalEnabledForStage = input<boolean>(false);
  public readonly hasEvaluationPanel = input<boolean>(false);
  public readonly ratingLevelTranslations = input<RatingLevelTranslations>();
  @ViewChild('cardBody') public cardBody: ElementRef;

  @Input() public result: InterviewResult & {
    offer?: Pick<OfferFull, 'kind' | 'status' | 'title'>;
    stage?: Pick<Stage, 'name'>;
  };
  @Input() public abilities? = {
    highLevelDataAccess: false,
    viewCandidates: false,
    manageCandidates: false,
  };

  public assignedPlacement?: PlacementInfo | null;

  public sanitizedNote: SafeHtml;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      showMore: false,
      showMoreEnabled: false,
      quickEditEnabled: false,
      deleteLoading: false,
      answers: [],
    });
  }

  public ngOnInit(): void {
    this.setAssignedPlacement();
    this.subscribeToEvents();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const answers =
      changes.result &&
      changes.result.currentValue &&
      changes.result.currentValue.interviewResultAnswers;

    if (answers) {
      this.setState({
        ...this.state,
        answers: answers.filter((answer: InterviewResultAnswer) => answer.questionKind !== 'info'),
      });
    }

    if ('result' in changes) {
      // eslint-disable-next-line ban/ban
      this.sanitizedNote = this.sanitizer.bypassSecurityTrustHtml(this.result.ratingNote!);
      this.cdRef.detectChanges();
    }
  }

  public ngAfterViewInit(): void {
    this.checkShowMore();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public openCandidate(result: InterviewResult): void {
    this.appNavigator.openCandidateDialog(result.candidateId);
  }

  public openOffer(result: InterviewResult): void {
    const { offer, talentPool } = result;
    if (!offer && !talentPool) return;
    const { kind, slug } = (offer || talentPool)!;

    const url =
      kind === OfferKind.Job ? `/offers/${slug}/pipeline` : `/talent_pools/${slug}/filters`;

    this.router.navigateByUrl(url);
  }

  public checkShowMore(): void {
    if (this.cardBody) {
      const height = this.cardBody.nativeElement.scrollHeight;

      if (height > this.MAX_VISIBLE_HEIGHT) {
        this.setState({ ...this.state, showMoreEnabled: true });
      }
    }
  }

  public toggleShowMore(): void {
    this.setState({ ...this.state, showMore: !this.state.showMore });
  }

  public editInterviewResult(): void {
    if (this.result.kind === InterviewKind.QUICK) {
      if (this.hasEvaluationPanel()) {
        this.appNavigator.openCandidateDialog(this.candidate().id, {
          tab: 'interviews',
          openInterview: true,
          quickEvaluation: true,
          interviewEdit: true,
          interviewResultId: this.result.id,
        });
        return;
      }

      this.openQuickEvaluationForm();
    } else {
      this.appNavigator.openCandidateDialog(this.candidate().id, {
        tab: 'interviews',
        openInterview: true,
        interviewEdit: true,
        interviewResultId: this.result.id,
      });
    }
  }

  public close(): void {
    this.setState({ ...this.state, quickEditEnabled: false });
  }

  public canEdit(): boolean {
    const currentAdmin = this.currentAdmin();
    return !!(
      currentAdmin &&
      this.result.adminId === currentAdmin!.id &&
      this.abilities?.viewCandidates
    );
  }

  public delete(): void {
    const message = this.i18n.translate('app.interview.delete_message');
    const title = this.i18n.translate('app.interview.delete_title');

    this.openConfirmDialog(message, title, 'error').subscribe(confirmed => {
      if (confirmed) {
        this.deleteInterviewResult();
      }
    });
  }

  public openQuickEvaluationForm(): void {
    this.setState({ ...this.state, quickEditEnabled: true });
  }

  private openConfirmDialog(message: string, title: string, variant: string): Observable<boolean> {
    return this.dialogService.confirm(
      { title, message, variant, isCancelable: true, safeFooter: true },
      this.viewRef,
    );
  }

  private deleteInterviewResult(): void {
    this.setState({ ...this.state, deleteLoading: true });
    this.store.dispatch(
      InterviewResultsActions.deleteOnePessimistic({
        payload: {
          data: {
            id: this.result && this.result.id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private setAssignedPlacement(): void {
    const offer = this.result.offer || this.result.talentPool;
    this.assignedPlacement = offer && {
      status: offer.status,
      kind: offer.kind,
      title: offer.title,
      stageName: this.result?.stage?.name,
    };
  }

  private subscribeToEvents(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticSuccess,
          InterviewResultsActions.deleteOnePessimisticFailed,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.setState({ ...this.state, deleteLoading: false });
      });
  }
}
